import React, { useState, useCallback } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import * as Icons from "lucide-react";

const GROUP_META = {
  clients: { label: "Clients / family", color: "#1C4358", icon: "Users" },
  external: { label: "Partners / vendors", color: "#2C6480", icon: "Building2" },
  professionals: { label: "Professionals", color: "#7C3AED", icon: "Scale" },
};
const SKIP_LABEL = { recently_reminded: "Recently reminded", no_action: "No pending action", internal_only: "Internal-only" };
const FILTER_LABEL = { section: "Section", status: "Status", category: "Category", service_lane: "Service lane", branch_id: "Branch", signing_pending: "Signing pending", needs_review: "Needs review", q: "Search" };
const SECTION_LABEL = { needs_review: "Waiting for review", signing_pending: "Signing pending", professional_review: "Professional reviews", requested: "Requested uploads", shared_externally: "Shared externally" };

// PROMPT 13I/13J — staff "Remind All Stale" preview → confirm → bulk send. Honors active queue filters (13J).
export default function BulkRemindDialog({ filters = {}, onDone }) {
  const [open, setOpen] = useState(false);
  const [threshold, setThreshold] = useState("3");
  const [pv, setPv] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState("");

  const loadPreview = useCallback(async (days) => {
    setLoading(true); setPv(null);
    try {
      const params = { threshold_days: days, ...filters };
      const { data } = await api.get(`/doc-center/documents/bulk-remind/preview`, { params });
      setPv(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, [filters]);

  const openDialog = () => { setOpen(true); setNote(""); setThreshold("3"); loadPreview(3); };
  const changeThreshold = (v) => { setThreshold(v); loadPreview(Number(v)); };

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/doc-center/documents/bulk-remind`, { threshold_days: Number(threshold), confirm: true, message: note.trim() || undefined, filters });
      toast.success(data.message || `Sent ${data.sent} reminders.`);
      setOpen(false); onDone?.();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSending(false); }
  };

  const skippedTotal = pv ? Object.values(pv.skipped || {}).reduce((a, b) => a + b, 0) : 0;
  const appliedEntries = Object.entries(filters || {}).filter(([, v]) => v);

  return (
    <>
      <Button variant="outline" size="sm" className="h-9 border-[#7C3AED]/40 text-[#7C3AED] gap-1.5" onClick={openDialog} data-testid="bulk-remind-open">
        <Icons.BellRing size={15} /> Remind All Stale
      </Button>

      <Dialog open={open} onOpenChange={(o) => { if (!sending) setOpen(o); }}>
        <DialogContent data-testid="bulk-remind-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1C4358] flex items-center gap-2"><Icons.BellRing size={17} /> Remind all stale documents</DialogTitle>
            <DialogDescription className="text-[13px]">Nudge everyone with a pending document action that hasn't been reminded recently. The 24-hour duplicate guard is always respected.</DialogDescription>
          </DialogHeader>

          <div className="flex items-center justify-between gap-3">
            <label className="text-[13px] text-[#6B7280]">Stale threshold</label>
            <Select value={threshold} onValueChange={changeThreshold}>
              <SelectTrigger className="h-9 w-[180px]" data-testid="bulk-remind-threshold"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="3">Not reminded in 3 days</SelectItem>
                <SelectItem value="7">Not reminded in 7 days</SelectItem>
                <SelectItem value="14">Not reminded in 14 days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-start gap-2 flex-wrap text-[12px]" data-testid="bulk-remind-filters">
            <span className="text-[#6B7280] font-medium mt-0.5">Filters applied:</span>
            {appliedEntries.length === 0 ? (
              <span className="text-[#94A3B8]">None — all stale documents in your scope.</span>
            ) : appliedEntries.map(([k, v]) => (
              <span key={k} className="inline-flex items-center gap-1 rounded-full bg-[#EEF2F5] text-[#1C4358] px-2 py-0.5 font-medium" data-testid={`bulk-filter-${k}`}>
                {FILTER_LABEL[k] || k}: {k === "section" ? (SECTION_LABEL[v] || v) : String(v)}
              </span>
            ))}
          </div>

          {loading ? (
            <div className="py-8 text-center text-[13px] text-[#6B7280]"><Icons.Loader2 className="animate-spin inline mr-2" size={15} /> Checking…</div>
          ) : !pv ? null : (
            <div className="space-y-3" data-testid="bulk-remind-preview">
              <div className="rounded-lg bg-[#F4F8FB] border border-[#E6EDF2] p-3">
                <div className="text-[14px] font-semibold text-[#252221]" data-testid="bulk-remind-count">
                  {pv.will_send === 0 ? "Nothing stale to remind right now." : `${pv.will_send} reminder${pv.will_send === 1 ? "" : "s"} will be sent.`}
                </div>
                {pv.capped && (
                  <div className="text-[12px] text-[#B7791F] mt-1" data-testid="bulk-remind-capped">{pv.max} of {pv.eligible_count} eligible reminders will be sent. Use filters to narrow the list.</div>
                )}
                {pv.will_send > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {Object.entries(pv.groups).filter(([, n]) => n > 0).map(([g, n]) => {
                      const m = GROUP_META[g];
                      return (
                        <span key={g} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ backgroundColor: `${m.color}12`, color: m.color }} data-testid={`bulk-group-${g}`}>
                          {React.createElement(Icons[m.icon] || Icons.Users, { size: 11 })} {m.label}: {n}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>

              {skippedTotal > 0 && (
                <div className="text-[12px] text-[#6B7280]" data-testid="bulk-remind-skipped">
                  <span className="font-medium text-[#94A3B8]">Skipped:</span>{" "}
                  {Object.entries(pv.skipped).filter(([, n]) => n > 0).map(([k, n]) => `${SKIP_LABEL[k] || k} (${n})`).join(" · ")}
                </div>
              )}

              {pv.will_send > 0 && (
                <div>
                  <label className="text-[12px] text-[#6B7280]">Optional note (applies to all reminders)</label>
                  <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="text-[13px] mt-1" maxLength={1000}
                    placeholder="MovingMyMama has a document item waiting for your attention…" data-testid="bulk-remind-note" />
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={sending} data-testid="bulk-remind-cancel">Cancel</Button>
            <Button onClick={send} disabled={sending || !pv || pv.will_send === 0} className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white" data-testid="bulk-remind-send">
              {sending ? <Icons.Loader2 className="animate-spin mr-1" size={14} /> : <Icons.Send size={14} className="mr-1" />}
              {pv ? `Send ${pv.will_send} reminder${pv.will_send === 1 ? "" : "s"}` : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
